/** OBS Overlay — 1920x1080 transparent overlay for browser source. No auth. */

import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { RaleySocket } from "@/api/ws";
import { OVERLAY_STYLES } from "@/overlay/styles";
import { ThemedOverlay } from "@/overlay/ThemedOverlay";
import type { OBSState, WSMessage, OverlayStyle } from "@/types";

const API_BASE = import.meta.env.VITE_API_URL ?? "";
const WS_BASE = import.meta.env.VITE_WS_URL ?? `ws://${window.location.host}`;

export function OBSOverlayPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<OBSState | null>(null);
  const [overlayStyle, setOverlayStyle] = useState<OverlayStyle>("steel");
  const socketRef = useRef<RaleySocket | null>(null);

  const [scale, setScale] = useState(1);

  const updateScale = useCallback(() => {
    const s = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
    setScale(s);
  }, []);

  // Set transparent body for OBS browser source + viewport scaling
  useEffect(() => {
    document.body.classList.add("obs-page");
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => {
      document.body.classList.remove("obs-page");
      window.removeEventListener("resize", updateScale);
    };
  }, [updateScale]);

  // Initial fetch
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/obs/${token}/state`)
      .then((r) => r.json())
      .then((data: OBSState) => {
        setState(data);
        if (data.overlay_style) setOverlayStyle(data.overlay_style);
      });
  }, [token]);

  // WebSocket for live updates
  useEffect(() => {
    if (!token) return;
    const socket = new RaleySocket(`${WS_BASE}/api/obs/${token}/ws`);
    socketRef.current = socket;
    socket.connect();

    socket.subscribe((msg: WSMessage) => {
      if (msg.type === "live_state") {
        if ("overlay_style" in msg && msg.overlay_style) {
          setOverlayStyle(msg.overlay_style as OverlayStyle);
        }
        // Re-fetch full state for accuracy
        fetch(`${API_BASE}/api/obs/${token}/state`)
          .then((r) => r.json())
          .then(setState);
      }
      if (msg.type === "scores_update") {
        setState((prev) =>
          prev ? { ...prev, scores: msg.scores, finals: msg.finals, attempt_finals: msg.attempt_finals, rider_attempt_scores: msg.rider_attempt_scores } : prev,
        );
      }
      if (msg.type === "attempt_update" || msg.type === "rider_attempt_update") {
        setState((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            riders: prev.riders.map((r) =>
              r.id === msg.rider_id
                ? { ...r, current_attempt: msg.current_attempt }
                : r,
            ),
          };
        });
      }
    });

    return () => socket.disconnect();
  }, [token]);

  if (!state || !state.is_live) {
    return <div className="obs-overlay" />;
  }

  const theme = OVERLAY_STYLES[overlayStyle] ?? OVERLAY_STYLES.steel;

  // Build heat banner text with division name
  const heatBanner = state.heat
    ? state.heat.division_name
      ? `${state.heat.division_name} — ${state.heat.name}`
      : state.heat.name
    : "";

  // Map state to ThemedOverlay props
  const riders = state.riders.map((r) => ({
    id: r.id,
    name: r.name,
    initials: r.name.split(" ").map((w) => w[0]).join("").slice(0, 2),
    colour: r.fav_colour ?? "#3b82f6",
    score: state.finals[r.id] ?? null,
    hits: 0,
    nickname: r.nickname,
    number: r.number,
    hasPhoto: r.has_photo,
    currentAttempt: r.current_attempt,
    maxAttempts: state.heat?.max_attempts ?? 1,
    attemptScores: state.attempt_finals?.[r.id] ?? {},
  }));

  const focusRider = state.focus_rider ? {
    id: state.focus_rider.id,
    name: state.focus_rider.name,
    initials: state.focus_rider.name.split(" ").map((w) => w[0]).join("").slice(0, 2),
    colour: state.focus_rider.fav_colour ?? "#3b82f6",
    score: state.finals[state.focus_rider.id] ?? null,
    hits: 0,
    nickname: state.focus_rider.nickname,
    number: state.focus_rider.number,
    funFact: state.focus_rider.fun_fact,
    hasPhoto: state.focus_rider.has_photo,
  } : null;

  // Build scorecard data if showing scores for a rider
  let scorecardRider = null;
  let scorecardJudges: Array<{ name: string; value: number }> | undefined;
  let scorecardFinal: number | null = null;
  let scorecardAttemptScores: Record<string, Record<string, number>> | undefined;
  let scorecardAttemptFinals: Record<string, number | null> | undefined;
  if (state.scores_rider_id && !state.show_all_scores) {
    const rid = state.scores_rider_id;
    scorecardRider = riders.find((r) => r.id === rid) ?? null;
    const riderScores = state.scores[rid] ?? {};
    scorecardJudges = Object.entries(riderScores).map(([, value], i) => ({
      name: `Judge ${i + 1}`,
      value,
    }));
    scorecardFinal = state.finals[rid] ?? null;
    scorecardAttemptScores = state.rider_attempt_scores?.[rid];
    scorecardAttemptFinals = state.attempt_finals?.[rid];
  }

  return (
    <div className="obs-overlay" style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
      <ThemedOverlay
        theme={theme}
        heatName={heatBanner}
        competitionName={state.competition_name}
        riders={riders}
        focusRider={focusRider}
        scorecardRider={scorecardRider}
        scorecardJudges={scorecardJudges}
        scorecardFinal={scorecardFinal}
        scorecardModel={state.scoring_model}
        scorecardAttemptScores={scorecardAttemptScores}
        scorecardAttemptFinals={scorecardAttemptFinals}
        scorecardMaxAttempts={state.heat?.max_attempts ?? 1}
        showLeaderboard={state.show_all_scores}
        showHeatScores={state.show_heat_scores}
        showRiderScores={state.show_rider_scores}
        showHeatBanner={state.show_current_heat}
        judgeCount={state.judge_count}
        allScores={state.scores}
        isFreeTier={state.owner_plan !== "pro"}
      />
    </div>
  );
}
